import React, {useCallback, useEffect, useRef, useState} from "react";
import "./App.scss";
import {GlobalContext, initialGlobalContext} from "./context/global.context";
import {RADIO_LINKS} from "./constants/links";
import axios from "axios";
import Hero from "./components/hero/hero";
import AboutSection from "./components/about-section/about-section";
import FooterBar from "./components/footer-bar/footer-bar";

const ReactHowler = require("react-howler");

const RETRY_INTERVAL = 10000;
const MAX_RETRY_ATTEMPTS = 18;
const WATCHDOG_INTERVAL = 8000;
const WATCHDOG_START_DELAY = 15000;

const App = () => {
    const howlerRef = useRef();
    const standbyHowlerRef = useRef();

    const [radioLink, setRadioLink] = useState("");
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isMute, setIsMute] = useState(false);
    const [volumeIndex, setVolumeIndex] = useState(4);
    const [connectionLost, setConnectionLost] = useState(false);
    const [retryCountdown, setRetryCountdown] = useState(0);
    const [retryKey, setRetryKey] = useState(0);
    const [radioError, setRadioError] = useState(false);
    const [statusMessages, setStatusMessages] = useState({});
    const [isPlayerActive, setIsPlayerActive] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [serverStatus, setServerStatus] = useState(null);
    const [routine, setRoutine] = useState([]);
    const [activeRoutine, setActiveRoutine] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const [standbyKey, setStandbyKey] = useState(0);
    const [isStandby, setIsStandby] = useState(false);

    const retryTimerRef = useRef(null);
    const countdownTimerRef = useRef(null);
    const retryAttemptRef = useRef(0);
    const linkFoundRef = useRef(false);
    const serverStatusRef = useRef(null);
    const userActionRef = useRef(false);
    const audioNodeCleanupRef = useRef(null);
    const watchdogIntervalRef = useRef(null);
    const watchdogStartDelayRef = useRef(null);
    const lastSeekRef = useRef(0);
    const loadTimeoutRef = useRef(null);

    const volumes = [0, 0.25, 0.5, 0.75, 1];

    const convertEESTToLocal = (eestTimeStr) => {
        const now = new Date();
        const [hour, min] = eestTimeStr.split(':').map(Number);
        const eestDate = new Date(now.toLocaleString('en-US', {timeZone: 'Europe/Bucharest'}));
        eestDate.setHours(hour, min, 0, 0);
        const localDate = new Date(now.toLocaleString('en-US', {timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone}));
        const eestOriginal = new Date(now.toLocaleString('en-US', {timeZone: 'Europe/Bucharest'}));
        const offsetDiff = localDate.getTime() - eestOriginal.getTime();
        const localTime = new Date(eestDate.getTime() + offsetDiff);
        return localTime.toLocaleTimeString('en-US', {hour: '2-digit', minute: '2-digit', hour12: false});
    };

    const convertIntervalToLocal = (eestInterval) => {
        const [start, end] = eestInterval.split('-');
        return `${convertEESTToLocal(start)}-${convertEESTToLocal(end)}`;
    };

    const isTimeInInterval = (timeInterval, eestNow) => {
        const [startTime, endTime] = timeInterval.split('-');
        const [startHour, startMin] = startTime.split(':').map(Number);
        const [endHour, endMin] = endTime.split(':').map(Number);
        const eestHour = eestNow.getHours();
        const eestMin = eestNow.getMinutes();
        const currentMinutes = eestHour * 60 + eestMin;
        const startMinutes = startHour * 60 + startMin;
        let endMinutes = endHour * 60 + endMin;
        if (endMinutes < startMinutes) {
            endMinutes += 24 * 60;
            if (currentMinutes < startMinutes) {
                return currentMinutes + 24 * 60 >= startMinutes && currentMinutes + 24 * 60 < endMinutes;
            }
        }
        return currentMinutes >= startMinutes && currentMinutes < endMinutes;
    };

    useEffect(() => {
        fetch("/json/statuses.json")
            .then((res) => res.json())
            .then((data) => setStatusMessages(data))
            .catch((err) => console.error("Failed to load statuses:", err));

        fetch("/json/routine.json")
            .then((res) => res.json())
            .then((data) => setRoutine(data))
            .catch((err) => console.error("Failed to load routine:", err));

        const readServerStatus = () => {
            const bodyText = document.body.textContent || document.body.innerText || "";
            if (bodyText.includes("PHATBEATZ_STATUS_ONLINE")) {
                serverStatusRef.current = "online";
                setServerStatus("online");
                return true;
            } else if (bodyText.includes("PHATBEATZ_STATUS_OFFLINE")) {
                serverStatusRef.current = "offline";
                setServerStatus("offline");
                return true;
            } else if (bodyText.includes("PHATBEATZ_STATUS_NOSOURCE")) {
                serverStatusRef.current = "nosource";
                setServerStatus("nosource");
                return true;
            }
            return false;
        };

        if (!readServerStatus()) {
            const checkInterval = setInterval(() => {
                if (readServerStatus()) clearInterval(checkInterval);
            }, 100);
            setTimeout(() => {
                clearInterval(checkInterval);
                if (!serverStatusRef.current) {
                    serverStatusRef.current = "online";
                    setServerStatus("online");
                }
            }, 5000);
        }
    }, []);

    useEffect(() => {
        if (routine.length === 0) return;
        const updateActiveRoutine = () => {
            const now = new Date();
            const eestTime = new Date(now.toLocaleString('en-US', {timeZone: 'Europe/Bucharest'}));
            const active = routine.find(item => isTimeInInterval(item.time, eestTime));
            setActiveRoutine(active || null);
        };
        updateActiveRoutine();
        const interval = setInterval(updateActiveRoutine, 60000);
        return () => clearInterval(interval);
    }, [routine]);

    const clearRetryTimers = useCallback(() => {
        if (retryTimerRef.current) {
            clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
        }
        if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
        }
    }, []);

    const attemptReconnect = useCallback(() => {
        if (!radioLink) return;
        if (retryAttemptRef.current >= MAX_RETRY_ATTEMPTS) {
            clearRetryTimers();
            setConnectionLost(false);
            setRetryCountdown(0);
            return;
        }
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            clearRetryTimers();
            setRetryCountdown(0);
            return;
        }
        retryAttemptRef.current += 1;
        setRetryCountdown(RETRY_INTERVAL / 1000);
        countdownTimerRef.current = setInterval(() => {
            setRetryCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(countdownTimerRef.current);
                    countdownTimerRef.current = null;
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        retryTimerRef.current = setTimeout(() => {
            clearRetryTimers();
            setRetryKey((k) => k + 1);
            setIsLoading(true);
            setConnectionLost(true);
        }, RETRY_INTERVAL);
    }, [radioLink, clearRetryTimers]);

    const handleConnectionLost = useCallback(() => {
        if (retryTimerRef.current) return; // retry already scheduled
        setConnectionLost(true);
        setIsPlaying(false);
        setIsLoading(false);
        setIsPaused(false);
        attemptReconnect();
    }, [attemptReconnect]);

    const handleReconnectSuccess = useCallback(() => {
        clearRetryTimers();
        setConnectionLost(false);
        setRetryCountdown(0);
        retryAttemptRef.current = 0;
    }, [clearRetryTimers]);

    const activateStandby = useCallback(() => {
        if (isStandby || connectionLost || !isPlayerActive) return;
        setStandbyKey(k => k + 1);
        setIsStandby(true);
    }, [isStandby, connectionLost, isPlayerActive]);

    const handleStandbyReady = useCallback(() => {
        if (!isPlayerActive) return;
        setIsStandby(false);
        setIsLoading(true);
        setIsPaused(false);
        setRetryKey(k => k + 1);
        handleReconnectSuccess();
    }, [isPlayerActive, handleReconnectSuccess]);

    const handleWatchdogStall = useCallback(() => {
        if (connectionLost || !isPlayerActive) return;
        setIsStandby(false);
        setIsPlaying(false);
        setIsLoading(true);
        setIsPaused(false);
        setRetryKey(k => k + 1);
    }, [connectionLost, isPlayerActive]);

    const handleConnectionLostRef = useRef(handleConnectionLost);
    useEffect(() => {
        handleConnectionLostRef.current = handleConnectionLost;
    }, [handleConnectionLost]);

    const activateStandbyRef = useRef(activateStandby);
    useEffect(() => {
        activateStandbyRef.current = activateStandby;
    }, [activateStandby]);

    const handleWatchdogStallRef = useRef(handleWatchdogStall);
    useEffect(() => {
        handleWatchdogStallRef.current = handleWatchdogStall;
    }, [handleWatchdogStall]);

    useEffect(() => {
        RADIO_LINKS.forEach((link) => {
            axios.get(link).then(() => {
                if (!linkFoundRef.current) {
                    linkFoundRef.current = true;
                    setRadioLink(link);
                }
            });
        });
    }, []);

    useEffect(() => {
        return () => { clearRetryTimers(); };
    }, [clearRetryTimers]);

    useEffect(() => {
        if (isPlaying) setRadioError(false);
    }, [isPlaying]);

    useEffect(() => {
        const mobile = navigator.maxTouchPoints > 0 || /Mobi|Android/i.test(navigator.userAgent);
        setIsMobile(mobile);
    }, []);

    useEffect(() => {
        return () => {
            if (audioNodeCleanupRef.current) {
                audioNodeCleanupRef.current();
                audioNodeCleanupRef.current = null;
            }
        };
    }, [retryKey]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState !== 'visible') return;
            if (!isPlayerActive || isPaused || connectionLost || isLoading) return;
            const howler = howlerRef.current?.howler;
            if (howler && !howler.playing()) {
                handleConnectionLostRef.current();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [isPlayerActive, isPaused, connectionLost, isLoading]);

    useEffect(() => {
        clearTimeout(loadTimeoutRef.current);
        if (!isLoading || !isPlayerActive) return;
        loadTimeoutRef.current = setTimeout(() => {
            handleConnectionLostRef.current();
        }, 20000);
        return () => clearTimeout(loadTimeoutRef.current);
    }, [isLoading, isPlayerActive]);

    useEffect(() => {
        const stop = () => {
            clearTimeout(watchdogStartDelayRef.current);
            clearInterval(watchdogIntervalRef.current);
            watchdogStartDelayRef.current = null;
            watchdogIntervalRef.current = null;
        };

        if (!isPlaying || !isPlayerActive || connectionLost) {
            stop();
            return stop;
        }

        watchdogStartDelayRef.current = setTimeout(() => {
            const getNode = () => howlerRef.current?.howler?._sounds?.[0]?._node;
            lastSeekRef.current = getNode()?.currentTime ?? 0;

            watchdogIntervalRef.current = setInterval(() => {
                const node = getNode();
                if (!node) return;
                const currentTime = node.currentTime;
                if (currentTime === lastSeekRef.current) {
                    handleWatchdogStallRef.current();
                } else {
                    lastSeekRef.current = currentTime;
                }
            }, WATCHDOG_INTERVAL);
        }, WATCHDOG_START_DELAY);

        return stop;
    }, [isPlaying, isPlayerActive, connectionLost]);

    useEffect(() => {
        const handleOnline = () => {
            if (connectionLost && isPlayerActive && !isPaused) {
                attemptReconnect();
            }
            if (connectionLost) {
                setConnectionLost(false);
            }
        };
        const handleOffline = () => { clearRetryTimers(); };
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [connectionLost, isPlayerActive, isPaused, attemptReconnect, clearRetryTimers]);

    useEffect(() => {
        if ('mediaSession' in navigator) {
            const localInterval = activeRoutine ? convertIntervalToLocal(activeRoutine.time) : "";
            navigator.mediaSession.metadata = new MediaMetadata({
                title: activeRoutine ? `${activeRoutine.description ? `${localInterval} ${activeRoutine.title}` : ""}` : "The Vibedigga Show",
                artist: activeRoutine ? activeRoutine.description || `${localInterval} ${activeRoutine.title}` : "PhatBeatz Online",
                album: 'Underground Hip-Hop',
                artwork: [{src: '/img/thumbnail.jpg', sizes: '300x300', type: 'image/jpeg'}]
            });
            navigator.mediaSession.setActionHandler('play', () => {
                if (!radioLink) return;
                if (serverStatusRef.current === "offline" || serverStatusRef.current === "nosource") return;
                setRadioError(false);
                setConnectionLost(false);
                retryAttemptRef.current = 0;
                setIsPlayerActive(true);
                setIsLoading(true);
                setIsPaused(false);
                setRetryKey((k) => k + 1);
            });
            navigator.mediaSession.setActionHandler('pause', () => {
                userActionRef.current = true;
                setIsPlaying(false);
                setIsLoading(false);
                setIsPaused(true);
                setConnectionLost(false);
                clearRetryTimers();
                retryAttemptRef.current = 0;
            });
        }
    }, [radioLink, activeRoutine]);

    const handlePlay = () => {
        if (!radioLink) return;
        if (serverStatus === "offline" || serverStatus === "nosource") return;
        setRadioError(false);
        setConnectionLost(false);
        retryAttemptRef.current = 0;
        setIsPlayerActive(true);
        setIsLoading(true);
        setIsPaused(false);
        setRetryKey((k) => k + 1);
    };

    const handleStop = () => {
        userActionRef.current = true;
        setIsStandby(false);
        setIsPlaying(false);
        setIsLoading(false);
        setIsPaused(false);
        setIsPlayerActive(false);
        setConnectionLost(false);
        clearRetryTimers();
        retryAttemptRef.current = 0;
        if ('mediaSession' in navigator) {
            navigator.mediaSession.playbackState = 'none';
        }
    };

    const handleMute = () => { setIsMute(!isMute); };

    const handleVolume = () => {
        const next = volumeIndex + 1 >= volumes.length ? 0 : volumeIndex + 1;
        setVolumeIndex(next);
    };

    const getRagStatus = () => {
        if (serverStatus === "offline") return "red_offline";
        if (serverStatus === "nosource") return "orange_nosource";
        if (connectionLost) return "red_retry";
        if (radioError) return "red";
        if (isLoading) return "yellow_loading";
        if (isPlaying) return "green";
        return "yellow";
    };

    const ragStatus = getRagStatus();

    const getServicePeriodMessage = () => {
        const sp = statusMessages.service_period;
        if (!sp) return null;
        const now = new Date();
        const eestNow = new Date(now.toLocaleString('en-US', {timeZone: 'Europe/Bucharest'}));
        const start = new Date(sp.start);
        const end = new Date(sp.end);
        if (eestNow >= start && eestNow < end) return sp.message;
        return null;
    };

    const SERVICE_PERIOD_STATUSES = new Set(["yellow", "red", "red_offline", "orange_nosource", "red_retry"]);

    const ragMessage = ragStatus === "red_retry"
        ? typeof navigator !== 'undefined' && navigator.onLine === false
            ? "No internet connection"
            : retryAttemptRef.current > 0
                ? `Reconnecting in ${retryCountdown}s (${retryAttemptRef.current}/${MAX_RETRY_ATTEMPTS})`
                : `Lost connection, retrying in ${retryCountdown}s`
        : (SERVICE_PERIOD_STATUSES.has(ragStatus) && getServicePeriodMessage()) || statusMessages[ragStatus] || "";

    const globalContext = {
        ...initialGlobalContext,
        isMobile,
        Howler: {playing: isPlaying, mute: isMute, loading: isLoading},
        isMute,
        isLoading,
        isPlaying,
        isPaused,
        volumeIndex,
        radioError,
        setRadioError,
        ragStatus,
        ragMessage,
        retryCountdown,
        connectionLost,
        routine,
        activeRoutine,
        convertIntervalToLocal,
        handlePlay,
        handleStop,
        handleMute,
        handleVolume,
    };

    const primaryOnPlay = () => {
        setIsLoading(false);
        setIsPlaying(true);
        setIsPaused(false);
        handleReconnectSuccess();
        if ('mediaSession' in navigator) {
            navigator.mediaSession.playbackState = 'playing';
        }

        if (audioNodeCleanupRef.current) {
            audioNodeCleanupRef.current();
            audioNodeCleanupRef.current = null;
        }

        const audioNode = howlerRef.current?.howler?._sounds?.[0]?._node;
        if (audioNode) {
            let isActive = true;

            const handleAudioPause = () => {
                if (!isActive) return;
                if (userActionRef.current) {
                    userActionRef.current = false;
                    return;
                }
                handleConnectionLostRef.current();
            };

            const handleWaiting = () => {
                if (!isActive) return;
                activateStandbyRef.current();
            };

            const handleCanPlay = () => {
                if (!isActive) return;
                setIsStandby(false);
            };

            if (isMobile) {
                audioNode.addEventListener('pause', handleAudioPause);
            }
            audioNode.addEventListener('waiting', handleWaiting);
            audioNode.addEventListener('canplay', handleCanPlay);

            audioNodeCleanupRef.current = () => {
                isActive = false;
                audioNode.removeEventListener('pause', handleAudioPause);
                audioNode.removeEventListener('waiting', handleWaiting);
                audioNode.removeEventListener('canplay', handleCanPlay);
            };
        }
    };

    return (
        <GlobalContext.Provider value={globalContext}>
            {isPlayerActive && radioLink && (isPlaying || isLoading) && (
                <ReactHowler
                    key={retryKey}
                    src={`${radioLink}/listen.mp3`}
                    preload={true}
                    html5={true}
                    playing={isPlaying || isLoading}
                    mute={isMute}
                    volume={volumes[volumeIndex]}
                    onPlay={primaryOnPlay}
                    onLoad={() => { handleReconnectSuccess(); }}
                    onLoadError={() => {
                        setIsLoading(false);
                        setIsPlaying(false);
                        handleConnectionLost();
                    }}
                    onPlayError={() => {
                        setIsLoading(false);
                        setIsPlaying(false);
                        setRadioError(true);
                        handleConnectionLost();
                    }}
                    onStop={() => {
                        setIsPlaying(false);
                        setIsLoading(false);
                    }}
                    ref={howlerRef}
                />
            )}

            {isStandby && radioLink && (
                <ReactHowler
                    key={standbyKey}
                    src={`${radioLink}/listen.mp3`}
                    preload={true}
                    html5={true}
                    playing={true}
                    mute={isMute}
                    volume={volumes[volumeIndex]}
                    onPlay={handleStandbyReady}
                    onLoadError={() => { setIsStandby(false); }}
                    onPlayError={() => { setIsStandby(false); }}
                    ref={standbyHowlerRef}
                />
            )}

            <Hero/>
            <AboutSection/>
            <FooterBar/>
        </GlobalContext.Provider>
    );
};

export default App;
